import touchesFile from '../../../shared/contracts/festival-touches.json';
import { festivalDaysFor, type FestivalId } from './festivalDates';
import { festivalRegion, type FestivalRegion } from './festivalRegion';
import { MOON_SLACK, OCCASIONS, isEveryday } from './greetings';

// Festival mode (docs/indian-touches.md, D309–D312, #49): on a festival's
// own days the home header carries a small drawn touch, and on the
// festivals of lamps the loader's window light becomes a diya flame. The
// shapes live in shared/contracts/festival-touches.json so Android and iOS
// draw exactly the same; the dates come only from festival-dates.json.

export type TouchId =
  | 'diya'
  | 'gulal'
  | 'crescent'
  | 'star'
  | 'kite'
  | 'bonfire'
  | 'pongal-pot'
  | 'toran'
  | 'pookalam'
  | 'rakhi'
  | 'tricolour'
  | 'moon'
  | 'bunting';

interface ShapeBase {
  fill: string;
  stroke?: string;
  strokeWidth?: number;
  cap?: 'round';
  opacity?: number;
  /** Replaces whichever of fill or stroke the shape paints, in dark mode. */
  dark?: string;
  /** The diya's flame: sways gently unless reduced motion is on. */
  flicker?: boolean;
}

export interface CircleShape extends ShapeBase {
  type: 'circle';
  cx: number;
  cy: number;
  r: number;
}

export interface PathShape extends ShapeBase {
  type: 'path';
  d: string;
}

export type TouchShape = CircleShape | PathShape;

interface FlamePart {
  d: string;
  fill: string;
}

interface TouchesFile {
  viewBox: number;
  generic: TouchId;
  quiet: string[];
  festivals: Record<string, TouchId>;
  loaderFlame: { touch: TouchId; outer: FlamePart; inner: FlamePart };
  touches: Record<TouchId, TouchShape[]>;
}

const FILE = touchesFile as unknown as TouchesFile;

export const TOUCH_VIEWBOX = FILE.viewBox;
export const TOUCHES: Readonly<Record<TouchId, readonly TouchShape[]>> =
  FILE.touches;
export const FESTIVAL_TOUCHES: Readonly<Record<string, TouchId>> =
  FILE.festivals;
export const GENERIC_TOUCH: TouchId = FILE.generic;
export const QUIET_FESTIVALS: readonly string[] = FILE.quiet;
export const LOADER_FLAME = FILE.loaderFlame;

/** Where the diya's flame sways from (its base, on the wick), D310. */
export const FLAME_ORIGIN = { x: 16, y: 18.5 } as const;
/** One sway, each way (2.4 s, ease in-out, back and forth). */
export const FLAME_SWAY_MS = 2400;

/** The colour a shape paints with in this mode: fill, else stroke. */
export function shapePaint(
  shape: TouchShape,
  dark: boolean
): { fill: string; stroke?: string } {
  const paintsFill = shape.fill !== 'none';
  if (!dark || !shape.dark) return { fill: shape.fill, stroke: shape.stroke };
  return paintsFill
    ? { fill: shape.dark, stroke: shape.stroke }
    : { fill: shape.fill, stroke: shape.dark };
}

export interface FestivalTouch {
  festival: FestivalId;
  touch: TouchId;
}

const dayNumber = (year: number, month: number, date: number) =>
  Math.round(Date.UTC(year, month - 1, date) / 86_400_000);
const parseDay = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return dayNumber(y!, m!, d!);
};

/**
 * Whether `now` (the device's local date) is one of the festival's own days
 * in `region` (D309): its first day to its last, a day wider each way for
 * the moon-sighted ones (Chand Raat before, a late sighting after). Not the
 * greeting window: a decoration days early reads as a mistake.
 */
export function isFestivalModeDay(
  id: FestivalId,
  now: Date,
  region: FestivalRegion
): boolean {
  const today = dayNumber(now.getFullYear(), now.getMonth() + 1, now.getDate());
  const slack = MOON_SLACK[id] ?? 0;
  // Last year's or next year's festival can reach across New Year.
  for (const year of [
    now.getFullYear() - 1,
    now.getFullYear(),
    now.getFullYear() + 1,
  ]) {
    const days = festivalDaysFor(region, id, year);
    if (!days) continue;
    if (
      today >= parseDay(days.first) - slack &&
      today <= parseDay(days.last) + slack
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Today's festival touch, or null (D309, D310): among the festivals on one
 * of their own days, in greetings.json's order and skipping the quiet ones,
 * the first with a touch of its own, else the first with the generic one.
 */
export function festivalTouchOn(
  now = new Date(),
  region: FestivalRegion = festivalRegion(now)
): FestivalTouch | null {
  let generic: FestivalTouch | null = null;
  for (const occasion of OCCASIONS) {
    if (isEveryday(occasion)) continue;
    const id = occasion.id as FestivalId;
    if (QUIET_FESTIVALS.includes(id)) continue;
    if (!isFestivalModeDay(id, now, region)) continue;
    const own = FESTIVAL_TOUCHES[id];
    if (own) return { festival: id, touch: own };
    generic ??= { festival: id, touch: GENERIC_TOUCH };
  }
  return generic;
}

/** Whether the loader's window light is a diya flame today (D311). */
export function loaderFlameOn(touch: FestivalTouch | null): boolean {
  return touch?.touch === LOADER_FLAME.touch;
}
